import React from 'react'
import { Box, FormHelperText, FormLabel } from '@mui/material'
import { resolveFieldSurface } from './FieldSurfaceContext'
import type { FieldSurface } from './fieldSurfaces/types'
import type { FormFieldSize } from './formFieldTokens'
import {
  getFormFieldAboveLabelSx,
  getFormFieldLabelRowSx,
  getFormFieldShellSx,
  KIOT_FORM_FIELD_CLASS,
  mergeSx,
} from './formFieldStyles'
import type { BaseFormFieldProps } from './formFieldTypes'

export type FormFieldShellProps = Pick<
  BaseFormFieldProps,
  'label' | 'labelAction' | 'required' | 'disabled' | 'error' | 'helperText' | 'fullWidth' | 'sx' | 'id'
> & {
  size?: FormFieldSize
  surface?: FieldSurface
  children: React.ReactNode
}

/** Bọc field khi `labelLayout === 'above'` — nhãn trên ô, không lồng FormControl (tránh xung đột TextField). */
export function FormFieldShell({
  label,
  labelAction,
  required,
  disabled,
  error,
  helperText,
  fullWidth = true,
  sx,
  id,
  surface: surfaceProp,
  children,
}: FormFieldShellProps) {
  const surface = resolveFieldSurface(surfaceProp)
  const showLabelRow = Boolean(label || labelAction)

  return (
    <Box
      className={KIOT_FORM_FIELD_CLASS}
      sx={mergeSx(
        {
          width: fullWidth ? '100%' : 'auto',
          pointerEvents: disabled ? 'none' : undefined,
        },
        getFormFieldShellSx(surface, sx),
      )}
    >
      {showLabelRow ? (
        <Box sx={getFormFieldLabelRowSx(surface)}>
          {label ? (
            <FormLabel htmlFor={id} required={required} error={error} sx={getFormFieldAboveLabelSx(surface)}>
              {label}
            </FormLabel>
          ) : (
            <span />
          )}
          {labelAction ?? null}
        </Box>
      ) : null}
      {children}
      {helperText ? (
        <FormHelperText
          error={error}
          sx={(theme) => ({
            mx: 0,
            mt: 0.5,
            ...(error ? { color: `${theme.palette.error.main} !important` } : {}),
          })}
        >
          {helperText}
        </FormHelperText>
      ) : null}
    </Box>
  )
}
